import { ApolloLink, FetchResult, Operation } from '@apollo/client'
import { getMainDefinition, Observable } from '@apollo/client/utilities'
import { actionTracking } from 'utils/posthog'

import { requestIdHeader } from './headers'

type Status = 'success' | 'error' | 'graphql_error'

const now = () =>
  typeof performance !== 'undefined' ? performance.now() : Date.now()

const getOperationType = (operation: Operation): string => {
  const def = getMainDefinition(operation.query)
  return def.kind === 'OperationDefinition' ? def.operation : 'unknown'
}

const capture = (
  operation: Operation,
  startMs: number,
  status: Status,
  error?: unknown
) => {
  const requestId = operation.getContext()?.headers?.[requestIdHeader]
  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && error && 'message' in error
      ? String((error as { message: unknown }).message)
      : undefined

  actionTracking({
    action: 'client_graphql_timing',
    properties: {
      operation: operation.operationName,
      operation_type: getOperationType(operation),
      duration_ms: Math.round(now() - startMs),
      status,
      request_id: requestId,
      error_message: errorMessage
    }
  })
}

// Position this link as outer as possible (right after requestIDLink) so the
// captured duration reflects total user-perceived time, including retries.
export const timingLink = new ApolloLink((operation, forward) => {
  const start = now()

  return new Observable<FetchResult>(observer => {
    let receivedError = false
    const subscription = forward(operation).subscribe({
      next: result => {
        if (result.errors && result.errors.length > 0) {
          receivedError = true
          capture(operation, start, 'graphql_error', result.errors[0])
        }
        observer.next(result)
      },
      error: err => {
        receivedError = true
        capture(operation, start, 'error', err)
        observer.error(err)
      },
      complete: () => {
        if (!receivedError) {
          capture(operation, start, 'success')
        }
        observer.complete()
      }
    })
    return () => subscription.unsubscribe()
  })
})
